/**
 * 카테고리 LP 몰로코 API — 유닛광고(lp/category) / similar / recommend
 */
import { post } from '@apis/api';
import type { MolocoAdLPProductResponse } from '@models/moloco';
import type { Product } from '@wconcept/wconcept-common/models/renewal/product';
import { uuidv4 } from '@wconcept/wconcept-common/utils/commonUtils';
import { getCookieWckDisplayMktSID } from '@wconcept/wconcept-common/utils/cookie';
import { getMolocoCustNo } from '@utils/domainUtil';

const gatewayBaseURL = process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_URL;
const gatewayOptions = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'DISPLAY-API-KEY': process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_KEY || '',
  },
};

export const requestCategoryMolocoAdLpProductList = async ({
  pageId,
  categoryName,
  mediumCd,
  largeCategory,
  middleCategory,
}: {
  pageId: string;
  categoryName: string;
  mediumCd: string;
  largeCategory: string;
  middleCategory: string;
}) => {
  const requestId = uuidv4();
  const sessionId = await getCookieWckDisplayMktSID();
  const userId = await getMolocoCustNo(false);
  const { baseData, data } = await post<MolocoAdLPProductResponse>({
    url: `/display/api/moloco/v1/lp/category`,
    data: {
      requestId,
      sessionId: sessionId ? encodeURIComponent(sessionId) : '',
      pageId,
      userId,
      category: {
        mediumCd,
        largeCategory,
        middleCategory,
        categoryName,
      },
    },
    options: gatewayOptions,
    baseUrl: gatewayBaseURL,
  });
  return { baseData, data };
};

export interface CategoryMolocoSimilarAd {
  campaignId?: string;
  campaignType?: string;
  impTrackerList?: string[] | null;
  clickTrackerList?: string[] | null;
  product?: Product;
}

interface CategoryMolocoSimilarResponse {
  result?: string;
  data?: CategoryMolocoSimilarAd[] | null;
  errorCode?: number;
  message?: string | null;
}

export interface CategoryMolocoRecommendProduct extends Product {
  adYn?: string | null;
  impTrackerList?: string[] | null;
  clickTrackerList?: string[] | null;
  imageUrlMobile?: string | null;
}

export interface CategoryMolocoRecommendResult {
  title?: string | null;
  productList?: CategoryMolocoRecommendProduct[] | null;
}

interface CategoryMolocoRecommendResponse {
  result?: string;
  data?: CategoryMolocoRecommendResult | null;
  errorCode?: number;
  message?: string | null;
}

export const requestCategoryMolocoRecommendProductList = async ({
  pageId,
  recommendItems,
  mediumCd,
  largeCategory,
  middleCategory,
  smallCategory,
  adAllowed = true,
}: {
  pageId: string;
  recommendItems: string[];
  mediumCd: string;
  largeCategory: string;
  middleCategory: string;
  smallCategory: string;
  /** 맞춤형 광고 허용 여부. 차단이면 몰로코 파라미터(requestId/sessionId/pageId) 없이 호출해 추천 데이터로만 받는다 */
  adAllowed?: boolean;
}): Promise<CategoryMolocoRecommendResult | null> => {
  const sessionId = adAllowed ? await getCookieWckDisplayMktSID() : '';
  const molocoParams = adAllowed
    ? {
        requestId: uuidv4(),
        sessionId: sessionId ? encodeURIComponent(sessionId) : '',
        pageId,
      }
    : {};
  const res = await post<
    CategoryMolocoRecommendResponse | CategoryMolocoRecommendResult
  >({
    url: `/display/api/moloco/v1/lp/category/recommend`,
    data: {
      ...molocoParams,
      recommendItems,
      category: {
        mediumCd,
        largeCategory,
        middleCategory,
        smallCategory,
      },
    },
    options: gatewayOptions,
    baseUrl: gatewayBaseURL,
  });
  if (!res) {
    return null;
  }
  if ('result' in res || 'data' in res) {
    if (res.result === 'SUCCESS' && res.data) {
      return res.data;
    }
    return null;
  }
  return res as CategoryMolocoRecommendResult;
};

export const requestCategoryMolocoSimilarProductList = async ({
  pageId,
  itemCd,
  mediumCd,
  largeCategory,
  middleCategory,
}: {
  pageId: string;
  itemCd: string;
  mediumCd: string;
  largeCategory: string;
  middleCategory: string;
}): Promise<CategoryMolocoSimilarAd[]> => {
  const requestId = uuidv4();
  const sessionId = await getCookieWckDisplayMktSID();
  const userId = await getMolocoCustNo(false);
  const res = await post<
    CategoryMolocoSimilarResponse | CategoryMolocoSimilarAd[]
  >({
    url: `/display/api/moloco/v1/lp/category/similar`,
    data: {
      requestId,
      sessionId: sessionId ? encodeURIComponent(sessionId) : '',
      pageId,
      userId,
      itemCd,
      category: {
        mediumCd,
        largeCategory,
        middleCategory,
      },
    },
    options: gatewayOptions,
    baseUrl: gatewayBaseURL,
  });
  if (Array.isArray(res)) {
    return res;
  }
  if (res?.result === 'SUCCESS' && Array.isArray(res.data)) {
    return res.data;
  }
  return [];
};
